import React, { useState, useEffect, useRef } from 'react';
import { getAddressSuggestionsWithLocation, reverseGeocode, getCurrentLocation } from '../../utils/geocoding';
import { showError } from '../../utils/toast';

interface AddressAutocompleteProps {
  value?: string | null;
  onChange: (address: string, lat: number, lng: number) => void;
  placeholder?: string;
  label?: string;
  required?: boolean;
  onLocationSelect?: (lat: number, lng: number) => void;
  initialLat?: number;
  initialLng?: number;
}

const AddressAutocomplete: React.FC<AddressAutocompleteProps> = ({
  value,
  onChange,
  placeholder = 'Enter address...',
  label,
  required = false,
  onLocationSelect,
  initialLat,
  initialLng
}) => {
  const [suggestions, setSuggestions] = useState<Array<{ address: string; lat: number; lng: number }>>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [inputValue, setInputValue] = useState(value);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Load initial address if coordinates are provided
  useEffect(() => {
    if (initialLat && initialLng && !value) {
      reverseGeocode(initialLat, initialLng).then(address => {
        if (address) {
          setInputValue(address);
          onChange(address, initialLat, initialLng);
        }
      });
    }
  }, [initialLat, initialLng]);

  // Update input value when value prop changes
  useEffect(() => {
    setInputValue(value);
  }, [value]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setInputValue(query);

    if (query.length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    setIsLoading(true);
    try {
      const results = await getAddressSuggestionsWithLocation(query);
      setSuggestions(results);
      setShowSuggestions(results.length > 0);
    } catch (error) {
      console.error('Error fetching address suggestions:', error);
      showError('Failed to fetch address suggestions');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUseCurrentLocation = async () => {
    setIsLoading(true);
    try {
      const location = await getCurrentLocation();
      const address = await reverseGeocode(location.lat, location.lng);

      if (address) {
        setInputValue(address);
        onChange(address, location.lat, location.lng);
        if (onLocationSelect) {
          onLocationSelect(location.lat, location.lng);
        }
      } else {
        showError('Could not determine address from your location');
      }
    } catch (error: any) {
      console.error('Error getting current location:', error);
      showError(error.message || 'Failed to get your current location');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSuggestionClick = (suggestion: { address: string; lat: number; lng: number }) => {
    setInputValue(suggestion.address);
    setShowSuggestions(false);
    onChange(suggestion.address, suggestion.lat, suggestion.lng);
    if (onLocationSelect) {
      onLocationSelect(suggestion.lat, suggestion.lng);
    }
  };

  const handleInputFocus = () => {
    if (suggestions.length > 0) {
      setShowSuggestions(true);
    }
  };

  return (
    <div ref={wrapperRef} className="w-full relative">
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={inputValue || ''}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          placeholder={placeholder}
          required={required}
          className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
        />
        {isLoading && (
          <div className="absolute right-3 top-2.5">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-green-600"></div>
          </div>
        )}
        {!isLoading && inputValue && (
          <button
            type="button"
            onClick={() => {
              setInputValue('');
              onChange('', 0, 0);
            }}
            className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
          >
            ✕
          </button>
        )}
      </div>

      {/* Use Current Location Button */}
      <div className="mt-2">
        <button
          type="button"
          onClick={handleUseCurrentLocation}
          disabled={isLoading}
          className="text-sm text-blue-600 hover:text-blue-800 disabled:text-gray-400 flex items-center"
        >
          <span className="mr-1">📍</span>
          {isLoading ? 'Getting location...' : 'Use current location'}
        </button>
      </div>

      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {suggestions.map((suggestion, index) => (
            <button
              key={index}
              type="button"
              onClick={() => handleSuggestionClick(suggestion)}
              className="w-full text-left px-4 py-2 hover:bg-gray-100 focus:bg-gray-100 focus:outline-none transition-colors"
            >
              <div className="flex items-start">
                <span className="text-gray-400 mr-2">📍</span>
                <span className="text-sm text-gray-700">{suggestion.address}</span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default AddressAutocomplete;
